/**
 * Elle değerleme: fiziki altın, halka açık olmayan pay, beslemede olmayan
 * tahvil. Aşama 2'de `unresolved` durumuna düşen araçlar da buraya akacak.
 * `AssetBinding`'in "manual" dalıyla `label` üzerinden eşleşir.
 */

import { ElleDegerlemeYolu } from "../kabiliyet/IceAktarYolu";
import { useCallback, useState } from "react";
import type { ManualValuation } from "@parafon/contracts/local-api";
import { isKnownCurrency } from "@parafon/contracts";
import { M } from "../dil";
import { formatDecimal } from "../lib/decimal";
import { formatIsoDate, todayIso } from "../lib/format";
import { newId } from "../lib/id";
import { useRepositoryContext } from "../repo/RepositoryContext";
import { useAsync, useMutation } from "../repo/useAsync";
import { Confirm } from "../ui/Modal";
import { DecimalInput, readDecimalField } from "../ui/DecimalInput";
import { CurrencySelect } from "../ui/CurrencySelect";
import {
  Button,
  DateInput,
  EmptyState,
  ErrorBlock,
  Field,
  InlineError,
  LoadingBlock,
  NumberCell,
  Screen,
  TextArea,
  TextInput,
} from "../ui/primitives";

export function ManualValuationsScreen() {
  const { repo, revision, invalidate } = useRepositoryContext();
  const [deleting, setDeleting] = useState<ManualValuation | null>(null);
  const remove = useMutation();

  const load = useCallback(() => repo.manualValuations.list(), [repo]);
  const { state, reload } = useAsync(load, [repo, revision]);

  const onDelete = async (): Promise<void> => {
    if (deleting === null) return;
    const ok = await remove.run(() => repo.manualValuations.remove(deleting.id));
    if (ok) {
      setDeleting(null);
      invalidate();
    }
  };

  return (
    <Screen
      title={M.izleme.elleDeger.baslik}
      description={M.izleme.elleDeger.aciklama}
      toolbar={<ElleDegerlemeYolu hedef="fiyatlar" />}
    >
      <div className="grid gap-4 p-4 lg:grid-cols-[minmax(0,22rem)_minmax(0,1fr)]">
        <ValuationForm onSaved={invalidate} />

        <div className="pf-card min-w-0" data-kart-bolum="liste">
          {state.status === "loading" && state.data === undefined ? (
            <LoadingBlock label={M.izleme.elleDeger.yukleniyor} />
          ) : state.status === "error" ? (
            <ErrorBlock error={state.error} onRetry={reload} />
          ) : (state.data ?? []).length === 0 ? (
            <EmptyState
              title={M.izleme.elleDeger.kayitYok}
              description={M.izleme.elleDeger.kayitYokMetin}
            />
          ) : (
            <div className="overflow-x-auto">
              <table className="pf-table">
                <caption className="sr-only">{M.izleme.elleDeger.tabloBasligi}</caption>
                <thead>
                  <tr>
                    <th scope="col">{M.izleme.elleDeger.etiket}</th>
                    <th scope="col" className="pf-num">
                      {M.izleme.elleDeger.tarih}
                    </th>
                    <th scope="col" className="pf-num">
                      {M.izleme.elleDeger.deger}
                    </th>
                    <th scope="col">{M.izleme.elleDeger.not}</th>
                    <th scope="col" className="w-20 text-right">
                      {M.izleme.elleDeger.islem}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {(state.data ?? []).map((v) => (
                    <tr key={v.id}>
                      <td className="font-medium text-ink">{v.label}</td>
                      <td className="pf-num">{formatIsoDate(v.date)}</td>
                      <td className="pf-num">
                        <NumberCell currency={v.currency}>{formatDecimal(v.value, { fractionDigits: 2 })}</NumberCell>
                      </td>
                      <td className="max-w-60 truncate text-muted">{v.note ?? "—"}</td>
                      <td className="text-right">
                        <Button variant="danger" className="!h-6 !px-2 text-[12px]" onClick={() => setDeleting(v)}>
                          {M.ortak.sil}
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      <Confirm
        open={deleting !== null}
        onOpenChange={(open) => !open && setDeleting(null)}
        title={M.izleme.elleDeger.silOnayBaslik}
        body={
          <>
            <p>
              <strong className="text-ink">{deleting?.label}</strong> · {formatIsoDate(deleting?.date)}{" "}
              {M.izleme.elleDeger.silOnaySonEk}
            </p>
            <InlineError error={remove.error} />
          </>
        }
        confirmLabel={M.ortak.sil}
        onConfirm={() => void onDelete()}
        pending={remove.pending}
      />
    </Screen>
  );
}

function ValuationForm({ onSaved }: { readonly onSaved: () => void }) {
  const { repo } = useRepositoryContext();
  const save = useMutation();
  const [label, setLabel] = useState("");
  const [date, setDate] = useState(todayIso());
  const [value, setValue] = useState("");
  const [currency, setCurrency] = useState("TRY");
  const [note, setNote] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});

  const submit = async (): Promise<void> => {
    const next: Record<string, string> = {};
    if (label.trim() === "") next["label"] = M.izleme.elleDeger.etiketZorunlu;
    if (date === "") next["date"] = M.izleme.elleFiyat.tarihZorunlu;
    if (!isKnownCurrency(currency)) next["currency"] = M.izleme.elleFiyat.paraBirimiSecin;
    const read = readDecimalField(value, { required: true });
    if (!read.ok) next["value"] = read.message;
    setErrors(next);
    if (Object.keys(next).length > 0 || !read.ok || read.value === undefined) return;

    const ok = await save.run(() =>
      repo.manualValuations
        .upsert({
          id: newId(),
          label: label.trim(),
          date,
          value: read.value as string,
          currency: currency.trim().toUpperCase(),
          ...(note.trim() === "" ? {} : { note: note.trim() }),
        })
        .then(() => undefined),
    );
    if (ok) {
      setValue("");
      setNote("");
      onSaved();
    }
  };

  const err = (k: string): { error?: string } => {
    const v = errors[k];
    return v === undefined ? {} : { error: v };
  };

  return (
    <form
      className="pf-card space-y-3 p-3" data-kart-bolum="form"
      aria-label={M.izleme.elleDeger.formEtiketi}
      noValidate
      onSubmit={(e) => {
        e.preventDefault();
        void submit();
      }}
    >
      <p className="text-[13px] font-semibold text-ink">{M.izleme.elleDeger.degerlemeKaydet}</p>

      <Field
        label={M.izleme.elleDeger.varlikEtiketi}
        htmlFor="mv-label"
        required
        hint={M.izleme.elleDeger.etiketIpucu}
        {...err("label")}
      >
        <TextInput
          id="mv-label"
          value={label}
          placeholder={M.izleme.elleDeger.etiketOrnek}
          aria-invalid={errors["label"] !== undefined}
          onChange={(e) => setLabel(e.currentTarget.value)}
        />
      </Field>

      <Field label={M.izleme.elleDeger.degerlemeTarihi} htmlFor="mv-date" required {...err("date")}>
        <DateInput id="mv-date" value={date} onChange={(e) => setDate(e.currentTarget.value)} />
      </Field>

      <DecimalInput label={M.izleme.elleDeger.deger} value={value} onChange={setValue} required currency={currency} {...err("value")} />

      <Field label={M.izleme.elleDeger.paraBirimi} htmlFor="mv-cur" required {...err("currency")}>
        <CurrencySelect
          id="mv-cur"
          className="pf-fig"
          value={currency}
          invalid={errors["currency"] !== undefined}
          onChange={setCurrency}
        />
      </Field>

      <Field label={M.izleme.elleDeger.not} htmlFor="mv-note" hint={M.izleme.elleDeger.notIpucu}>
        <TextArea id="mv-note" value={note} onChange={(e) => setNote(e.currentTarget.value)} />
      </Field>

      <InlineError error={save.error} />

      <Button type="submit" variant="primary" disabled={save.pending} className="w-full">
        {save.pending ? M.ortak.kaydediliyor : M.ortak.kaydet}
      </Button>
    </form>
  );
}
